import { ColumnDef } from "@tanstack/react-table";
import ButtonGroups, { BGItem } from "components/ButtonGroups";
import React from "react";
import { useTranslation } from "react-i18next";
import { ENVIRONMENTAL_CATEGORIES, GroupCategory } from "utils";
import { EditableTable } from "../components/EditableTable/EditableTable";
import { IndicatorsProps, epdEditorStore } from "../EPDEditorStore";
import { MODULES_LIST } from "../consts";
import Alert from "components/Alert";
import useIndicatorsTableColumns from "./useIndicatorsTableColumns";
import { observer } from "mobx-react-lite";
import Button from "components/Button";
import { IconSparkles, IconUpload } from "@tabler/icons-react";
import ImportModal from "./ImportModal";
import useFeatureFlag from "hooks/useFeatureFlag";
import clsx from "clsx";
import { toJS } from "mobx";

export const CONFORMITY_LIST = [
  { id: "A1", name: "DIN EN 15804+A1" },
  { id: "A2", name: "DIN EN 15804+A2" },
];

interface IndicatorsSavedState {
  [key: string]: IndicatorsProps[] | undefined;
}

const EnvironmentalIndicators = () => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = React.useState<BGItem>(
    ENVIRONMENTAL_CATEGORIES[0]
  );
  const [columnsList, setColumnsList] = React.useState<string[]>([]);
  const initialized = React.useRef(false);
  const [loading, setLoading] = React.useState(false);
  const [openImportModal, setOpenImportModal] = React.useState({
    state: false,
    mode: "",
  });
  const { isFeatureVisible } = useFeatureFlag();
  const [savedState, setSavedState] = React.useState<IndicatorsSavedState>({
    A1: undefined,
    A2: undefined,
  });

  const imported = toJS(epdEditorStore.importedIndicators);

  React.useEffect(() => {
    if (!imported?.count) return;

    buildTableData();
  }, [imported?.count]);

  function findAvailableIndicatorModuleValue(
    availableIndicators: IndicatorsProps[] | undefined,
    indicatorName: string,
    module: string
  ): string | number {
    return (
      availableIndicators?.find((ind) => ind.indicator === indicatorName)?.[
        module
      ] ?? ""
    );
  }

  async function buildTableData() {
    if (loading) return;
    setLoading(true);
    const currentConformity = epdEditorStore.data.conformity_name;

    let availableIndicators: IndicatorsProps[] | undefined;

    if (imported?.indicators) {
      availableIndicators = epdEditorStore.restructureIndicators([
        ...imported.indicators,
      ]);
      epdEditorStore.onChangeData("conformity_name", imported.conformity_name);
      epdEditorStore.setImportedIndicators(undefined);
    } else {
      availableIndicators = epdEditorStore.data.indicators;
    }

    const indicators = await epdEditorStore.fetchIndicators();

    let tableData: IndicatorsProps[] | undefined;
    if (savedState[currentConformity] && !imported?.indicators) {
      tableData = savedState[currentConformity];
    } else {
      tableData = indicators?.map((rec) => ({
        indicator: rec.name,
        unit: rec.unit,
        ...(Object.fromEntries(
          MODULES_LIST.map((module) => [
            module,
            findAvailableIndicatorModuleValue(
              availableIndicators,
              rec.name,
              module
            ),
          ])
        ) as Record<string, string>),
      }));
    }
    epdEditorStore.onChangeData("indicators", tableData);
    onChangeGroups(ENVIRONMENTAL_CATEGORIES[0]);
    setLoading(false);
  }

  React.useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    buildTableData();
  }, [epdEditorStore.data.conformity_name]);

  const onChangeGroups = (item: BGItem | undefined) => {
    if (!item) return;
    setColumnsList([
      "indicator",
      "unit",
      ...(item as GroupCategory).values.map((rec) => rec),
    ]);
    setSelectedCategory(item);
  };

  const onChangeConformity = (item: BGItem | undefined) => {
    if (!item) return;
    const previousConformity = epdEditorStore.data.conformity_name;
    setSavedState({
      ...savedState,
      [previousConformity]: epdEditorStore.data.indicators,
    });
    epdEditorStore.onChangeData("conformity_name", item.id);
    buildTableData();
  };

  const selectedConformity = CONFORMITY_LIST.find(
    (item) => item.id === epdEditorStore.data.conformity_name
  );

  const isAIImport = openImportModal.mode === "AI";
  const noEPDFile = !epdEditorStore.filesToUpload.length;
  const columns = useIndicatorsTableColumns(columnsList);

  return (
    <div className="border-gray-200 pb-6 border-t xl:border-t-0 pt-4 xl:pt-0">
      <div className="font-semibold text-base mb-3">
        {t("productEditor.environmentalIndicators")}
      </div>
      <Alert
        type="info"
        title={t("EPDEditor.indicatorsAlert")}
        className="bg-white border-none flex items-center !p-0 mb-4"
      />
      <ButtonGroups
        items={CONFORMITY_LIST}
        onChange={onChangeConformity}
        current={selectedConformity}
        containerClassName="w-full mb-1.5"
        label={t("EPDEditor.EPDStandard")}
        confirmTitle={t("productEditor.confirmModalTitle")}
        confirmButtonTitle={t("productEditor.changeReferenceUnit")}
        confirmDescriptionMesage={t("productEditor.changeReferenceUnitDesc")}
        required
        optionClsssName="h-[36px]"
        size="middle"
      />

      {isFeatureVisible("epd_upload_indicators") && (
        <div className="flex xl:flex-col">
          <Button
            type="link"
            className="text-indigo-700 hover:!text-indigo-500 !px-2"
            width="fit-content"
            leadingIcon={<IconSparkles />}
            onClick={() => setOpenImportModal({ state: true, mode: "AI" })}
            disable={noEPDFile}
            tooltip={noEPDFile ? t("EPDEditor.AIDisabledTooltip") : undefined}
          >
            {t("EPDEditor.AIImport")}
          </Button>
          <Button
            type="link"
            className="text-indigo-700 hover:!text-indigo-500 !px-2 xl:ml-0 ml-2"
            width="fit-content"
            leadingIcon={<IconUpload />}
            onClick={() => setOpenImportModal({ state: true, mode: "CSV" })}
          >
            {t("EPDEditor.CSVImport")}
          </Button>
        </div>
      )}

      <ButtonGroups
        items={ENVIRONMENTAL_CATEGORIES}
        current={selectedCategory}
        onChange={onChangeGroups}
        optionClsssName="!h-6"
        containerClassName="mb-2 mt-1.5"
      />
      {epdEditorStore.data.indicators && !loading && (
        <EditableTable<IndicatorsProps>
          data={epdEditorStore.data.indicators}
          columns={columns as ColumnDef<unknown, unknown>[]}
          onDataChange={(data) =>
            data && epdEditorStore.onChangeData("indicators", data)
          }
          columnClassName={clsx(
            "first-of-type:bg-gray-50 first-of-type:px-2 first-of-type:py-1 [&:nth-of-type(2)]:px-2 [&:nth-of-type(2)]:py-1",
            "[&:nth-of-type(2)]:!bg-gray-50 h-7 n+1:bg-gray-100"
          )}
        />
      )}

      <ImportModal
        open={openImportModal.state}
        close={() => setOpenImportModal({ state: false, mode: "" })}
        mode={openImportModal.mode}
        conformity={isAIImport ? selectedConformity?.id : undefined}
        file={isAIImport ? epdEditorStore.filesToUpload[0] : undefined}
        openConfirm={isAIImport}
      />
    </div>
  );
};

export default observer(EnvironmentalIndicators);
